import { ChangeDetectionStrategy, Component, ElementRef, OnInit, ViewEncapsulation, computed, effect, inject, input, linkedSignal, output, signal, untracked, viewChild } from '@angular/core';
import { Attachment, AttachmentType, FeatureFlags } from '@jms/model';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { WorkBuddySideDialogService } from '@workbuddy/ui/services';
import { AttachmentComponent } from '@workbuddy/ui/components/attachment';
import { AttachmentPreviewDialogComponent } from '@workbuddy/ui/components/attachment.preview.dialog';
import { iconMap } from '@workbuddy/ui/fields/upload/model';
import { ButtonIconComponent, ButtonDisplayStyle, ButtonAccent } from '@workbuddy/ui/v2/components/button';

import type { AgentModelTier, MentionItem, PendingFile, SessionContext, ShortcutHint } from '../../agent.types';
import { FILE_CONSTRAINTS, readPersistedModelTier, writePersistedModelTier } from '../../agent.types';
import { AgentUtility } from '../../agent.utility';
import { AgentModelTierPickerComponent } from '../model.tier.picker/model.tier.picker.component';

export type PromptInputMode = 'default' | 'mentioning' | 'filled' | 'running';

/** Tallest the textarea grows before it starts scrolling internally. */
const TEXTAREA_MAX_HEIGHT = 200;

/** Shortcut hints exposed for the shared hints bar */
export const PROMPT_INPUT_HINTS: ShortcutHint[] = [
  { text: 'Enter to send' },
  { text: 'Shift-Enter for new line' },
];

@Component({
  selector: 'agent-prompt-input',
  standalone: true,
  imports: [AttachmentComponent, ButtonIconComponent, AgentModelTierPickerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.disabled]': '$disabled()',
    '[class.streaming]': '$isStreaming()',
    '[class.filled]': '$mode() === "filled" || $mode() === "mentioning"',
  },
  templateUrl: './prompt.input.component.html',
  styleUrls: ['./prompt.input.component.less'],
})
export class AgentPromptInputComponent implements OnInit {

  protected readonly ButtonDisplayStyle = ButtonDisplayStyle;
  protected readonly ButtonAccent = ButtonAccent;

  private readonly dialogService = inject(WorkBuddySideDialogService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  // ─── Inputs ─────────────────────────────────────────────────────────────────

  public readonly $showAttachment = input<boolean>(true, { alias: 'showAttachment' });
  public readonly $showSessionContext = input<boolean>(false, { alias: 'showSessionContext' });
  public readonly $sessionContext = input<SessionContext | null>(null, { alias: 'sessionContext' });
  public readonly $isStreaming = input<boolean>(false, { alias: 'isStreaming' });
  public readonly $disabled = input<boolean>(false, { alias: 'disabled' });
  public readonly $pendingFiles = input<PendingFile[]>([], { alias: 'pendingFiles' });
  // Once a chat has started (existing thread or any sent message) the model tier is frozen for that
  // thread. The dropdown still opens and lists both tiers, but the non-selected tier is disabled with
  // a tooltip nudging the user to start a new chat. A brand-new empty chat stays fully unlocked.
  public readonly $modelLocked = input(false, { alias: 'modelLocked' });
  // The active thread's stored tier, or null for a new/empty chat or a pre-feature thread with no
  // recorded tier. When set, the composer shows + locks to it; when null, it follows the user's
  // persisted default. Drives $modelTier below so opening an existing chat restores its real tier.
  public readonly $threadModelTier = input<AgentModelTier | null>(null, { alias: 'threadModelTier' });
  // The current conversation's composer draft. Seeded into $text on mount and mirrored back on
  // every change, so text typed before a card overlays (and tears down) this input is preserved.
  public readonly $draft = input<string>('', { alias: 'draft' });
  // The active conversation (threadId, or the new-chat key). Drives a re-seed of $text whenever it
  // changes so switching to another thread starts fresh instead of inheriting this thread's text.
  public readonly $conversationKey = input<string>('', { alias: 'conversationKey' });

  // ─── Outputs ────────────────────────────────────────────────────────────────

  public send = output<{ text: string; files: PendingFile[]; mentions: MentionItem[]; context: SessionContext | null; modelTier: AgentModelTier }>();
  public stop = output<void>();
  public fileSelected = output<File[]>();
  public removeFile = output<string>();
  public mentionSearch = output<string>();
  public contextRemoved = output<void>();
  public draftChange = output<string>();

  // ─── Internal State ─────────────────────────────────────────────────────────

  public $text = signal('');
  public $isMentioning = signal(false);
  public $mentionQuery = signal('');
  public $mentions = signal<MentionItem[]>([]);

  // The higher-capability (heavy) tier is gated by a tenant feature flag. When off, the tier picker
  // is hidden and every send is forced to 'default'. The backend re-applies this gate and is the real
  // security boundary; this only keeps the UI honest so users never see a choice they cannot use.
  protected readonly $heavyModelEnabled = computed(() => ConfigService.environment?.featureFlags?.[FeatureFlags.AIHeavyModel] ?? false);

  // The composer's current tier. Holds only the coarse tier — never a model name. Follows the
  // active thread's stored tier when there is one (so opening an existing chat restores its real
  // tier), and otherwise the user's persisted default (a brand-new chat). It is a linkedSignal so
  // it re-seeds whenever the source changes (thread switch / new chat), while still accepting a
  // manual user pick in a new chat — that write survives until the source next changes. User picks
  // are also persisted to localStorage by onModelTierChanged so they carry into the next new chat.
  protected $modelTier = linkedSignal<AgentModelTier>(() => this.$threadModelTier() ?? readPersistedModelTier());

  private textareaRef = viewChild<ElementRef<HTMLTextAreaElement>>('textarea');
  private fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  // ─── Computed ───────────────────────────────────────────────────────────────

  public $mode = computed<PromptInputMode>(() => {

    if (this.$isStreaming()) return 'running';
    if (this.$isMentioning()) return 'mentioning';
    if (this.$text().trim().length > 0) return 'filled';
    return 'default';
  });

  public $readyFiles = computed(() => this.$pendingFiles().filter(f => f.status === 'ready'));

  /**
   * Display tiles for ready files. Images/videos render as a visual thumbnail; every other
   * file type has no thumbnail, so it renders as a compact chip with the file-type icon and
   * name. This avoids the empty grey card non-image files (pdf/csv/.msg/.eml) showed before.
   */
  public readonly $readyTiles = computed(() => this.$readyFiles().map(file => {

    const isImage = AgentUtility.isImageType(file.file.type);
    const isVideo = file.file.type.startsWith('video/');
    let type: AttachmentType = 'File';
    if (isImage) type = 'Image';
    else if (isVideo) type = 'Video';

    const name = file.file.name;
    const dot = name.lastIndexOf('.');
    const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';

    const attachment = {
      _id: file.id,
      type,
      path: file.blobUrl ?? '',
      thumbnail: isImage ? file.blobUrl : undefined,
      title: name,
      fileName: name,
    } as Attachment;

    return {
      id: file.id,
      name,
      isThumbnail: isImage || isVideo,
      icon: iconMap.get(ext) ?? 'fa-regular fa-file',
      ext: ext.replace('.', '').toUpperCase(),
      attachment,
    };
  }));

  /** Attachment[] for the shared components (the preview dialog navigates the full set). */
  public readonly $readyAttachments = computed<Attachment[]>(() => this.$readyTiles().map(tile => tile.attachment));

  public $uploadingFiles = computed(() => this.$pendingFiles().filter(f => f.status === 'uploading'));
  public $errorFiles = computed(() => this.$pendingFiles().filter(f => f.status === 'error'));

  public $hasAttachments = computed(() => this.$pendingFiles().length > 0);

  public $canSend = computed(() => {

    const mode = this.$mode();
    return (mode === 'filled' || mode === 'mentioning') && !this.$disabled();
  });

  public $acceptedExtensions = FILE_CONSTRAINTS.ACCEPTED_EXTENSIONS;

  // ─── Auto-resize Effect ─────────────────────────────────────────────────────

  constructor() {

    effect(() => {

      this.$text();
      this.applyComposerLayout();
    });

    // Mirror the live composer text into the shared sticky draft so what's typed survives
    // this input being torn down by an interview/approval card overlay, and follows the user
    // between surfaces. onSend clears $text to '', which propagates here and clears the draft.
    effect(() => {
      const text = this.$text();
      this.draftChange.emit(text);
    });

    // Re-seed when the CONVERSATION changes (thread switch / new chat) so a different thread starts
    // fresh and never inherits this thread's text. Tracks the conversation key, not the draft value,
    // so the per-keystroke echo back through $draft can't clobber what is being typed.
    effect(() => {
      this.$conversationKey();
      untracked(() => this.$text.set(this.$draft()));
    });
  }

  public ngOnInit(): void {

    // Restore any text typed before a card overlaid (and unmounted) this input.
    const draft = this.$draft();
    if (draft) this.$text.set(draft);
  }

  // ─── Public API (called by parent container) ────────────────────────────────

  public setText(value: string): void {

    this.$text.set(value);
  }

  public focus(): void {

    this.textareaRef()?.nativeElement.focus();
  }

  public clearText(): void {

    this.$text.set('');
  }

  public addMention(item: MentionItem): void {

    this.$mentions.update(mentions => [...mentions, item]);
  }

  // ─── Event Handlers ─────────────────────────────────────────────────────────

  public onTextInput(event: Event): void {

    if (this.$disabled()) return;

    const textarea = event.target as HTMLTextAreaElement;
    const value = textarea.value;
    this.$text.set(value);
    this.detectMention(value, textarea.selectionStart ?? value.length);
  }

  public onPaste(event: ClipboardEvent): void {

    if (this.$disabled()) return;

    const items = event.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind !== 'file') continue;
      if (!item.type.startsWith('image/')) continue;
      const file = item.getAsFile();
      if (file) files.push(file);
    }

    if (files.length === 0) return;

    // Preserve any plain text that came with the image (e.g. screenshot + caption);
    // preventDefault() suppresses the native textarea paste, so re-insert it manually.
    const pastedText = event.clipboardData?.getData('text/plain') ?? '';
    event.preventDefault();
    if (pastedText) {
      this.insertTextAtCursor(pastedText);
    }
    this.fileSelected.emit(files);
  }

  private insertTextAtCursor(text: string): void {

    const textarea = this.textareaRef()?.nativeElement;
    if (!textarea) {
      this.$text.update(current => current + text);
      return;
    }
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? start;
    const next = textarea.value.slice(0, start) + text + textarea.value.slice(end);
    this.$text.set(next);
    queueMicrotask(() => {
      const cursor = start + text.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  }

  public onKeydown(event: KeyboardEvent): void {

    if (this.$disabled()) return;

    // Skip while IME is composing a character — pressing Enter to confirm a CJK
    // candidate must not send the message. keyCode 229 is the legacy fallback
    // emitted by some browsers during composition.
    if (event.isComposing || event.keyCode === 229) return;

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSend();
      return;
    }

    if (event.key === 'Escape' && this.$isMentioning()) {
      this.$isMentioning.set(false);
      this.$mentionQuery.set('');
    }
  }

  public onSend(): void {

    if (!this.$canSend()) return;

    // Force 'default' when the heavy-model flag is off, even if a stale 'heavy' is still persisted.
    const modelTier: AgentModelTier = this.$heavyModelEnabled() ? this.$modelTier() : 'default';

    this.send.emit({
      text: this.$text().trim(),
      files: this.$readyFiles(),
      mentions: this.$mentions(),
      context: this.$sessionContext(),
      modelTier,
    });

    this.$text.set('');
    this.$mentions.set([]);
    this.$isMentioning.set(false);
    this.$mentionQuery.set('');
    this.resetTextareaHeight();
  }

  public onStop(): void {

    this.stop.emit();
  }

  public onModelTierChanged(tier: AgentModelTier): void {

    this.$modelTier.set(tier);
    writePersistedModelTier(tier);
  }

  public onUploadClick(): void {

    if (this.$disabled()) return;
    this.fileInputRef()?.nativeElement.click();
  }

  public onFileChange(event: Event): void {

    const input = event.target as HTMLInputElement;
    if (this.$disabled()) {
      input.value = '';
      return;
    }
    if (!input.files?.length) return;

    const files = Array.from(input.files);
    const validFiles = files.filter(f => f.size <= FILE_CONSTRAINTS.MAX_FILE_SIZE);

    if (validFiles.length > 0) {
      this.fileSelected.emit(validFiles);
    }

    input.value = '';
  }

  public onRemoveContext(): void {

    this.contextRemoved.emit();
  }

  public onRemoveFile(id: string): void {

    this.removeFile.emit(id);
  }

  public onThumbnailClick(file: PendingFile): void {

    if (file.status === 'error') {
      this.fileSelected.emit([file.file]);
    }
  }

  public onAttachmentClick(attachment: Attachment): void {

    this.dialogService.openPseudoPage(AttachmentPreviewDialogComponent, {
      closeOnEscape: true,
      closeOnBackdropClick: true,
      data: {
        attachments: this.$readyAttachments(),
        attachmentId: attachment._id,
        title: attachment.fileName ?? '',
      },
    });
  }

  // ─── Private Helpers ────────────────────────────────────────────────────────

  private detectMention(text: string, cursorPos: number): void {

    const textBeforeCursor = text.substring(0, cursorPos);
    const mentionMatch = textBeforeCursor.match(/@(\w*)$/);

    if (mentionMatch) {
      this.$isMentioning.set(true);
      this.$mentionQuery.set(mentionMatch[1]);
      this.mentionSearch.emit(mentionMatch[1]);
    } else {
      this.$isMentioning.set(false);
      this.$mentionQuery.set('');
    }
  }

  private resetTextareaHeight(): void {

    const textarea = this.textareaRef()?.nativeElement;
    if (textarea) {
      textarea.style.height = '';
    }
    this.host.nativeElement.classList.remove('multiline');
  }

  // ─── Composer layout (single-row vs stacked) ────────────────────────────────

  /**
   * Size the textarea to its content, and break the composer out of its compact
   * single-row form once the text needs a second line.
   *
   * The flip is one-way for the life of a message: stacking widens the textarea (it no
   * longer shares the row with the controls), so re-testing afterwards would find the
   * text fits again and flip back, wrap, and alternate on every keystroke. Staying
   * stacked until the composer is cleared or sent avoids that entirely. The class is
   * toggled on the host directly rather than via a host binding because it changes the
   * textarea's width and must land before the height below is measured.
   */
  private applyComposerLayout(): void {

    const textarea = this.textareaRef()?.nativeElement;
    if (!textarea) return;

    if (this.$text().length === 0) {
      this.resetTextareaHeight();
      return;
    }

    const styles = getComputedStyle(textarea);
    const lineHeight = parseFloat(styles.lineHeight) || 20;
    const padding = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    const host = this.host.nativeElement;

    textarea.style.height = 'auto';
    // Half a line of slack so sub-pixel rounding never reads as a second line.
    const wrapped = textarea.scrollHeight - padding > lineHeight * 1.5;
    if (wrapped) host.classList.add('multiline');

    textarea.style.height = Math.min(textarea.scrollHeight, TEXTAREA_MAX_HEIGHT) + 'px';
  }
}
